import { useState, useEffect, useMemo } from 'react'
import { supabase } from '../lib/supabase'
import { colors, formatNumber } from '../styles/tokens'
import { Button, Input, Select } from './FormControls'
import { calculateRebarSlab, MODULE_REGISTRY } from '../lib/quantityEngine'
import { useDraftPersistence } from '../lib/useDraftPersistence'

// =======================================================================
// RebarSlabModulePanel — the complete Rebar Slab engineering module,
// implementing the FINAL LOCKED Rebar Contract in full (no V1/V2):
//   - Unlimited dynamic reinforcement Layers
//   - full_slab / local extent (barLengthDim / distributionWidthDim)
//   - Hook/Bend allowance, optional Development Length override (manual only)
//   - Openings (Model A: simple bar deduction, no auto trimming)
//   - Theoretical / Theoretical+Lap / Procurement — estimator picks the
//     quantity basis explicitly per saved Output
//   - Full geometry_snapshot / engineering_inputs provenance
//
// This component lives OUTSIDE the Generic Module Framework's simple
// =======================================================================
// RebarSlabModulePanel — the ONE unified Rebar Slab engineering
// calculation module, implementing the FINAL LOCKED Rebar Contract in
// full (no V1/V2, no duplicate implementation). It is reusable from
// BOTH consumption paths:
//   - BOQ Engineering Modules (SetQuantityModal) -> writes the chosen
//     quantity to boq_item.resolved_quantity via recapture_quantity_source()
//   - Quantification / Resource Quantity workflow (QuantificationPage)
//     -> creates a resource_quantity_output via record_resource_quantity_output()
// This component itself never decides which RPC to call — the caller
// supplies `onSave(payload)`, where payload carries the calculated
// value/unit/geometry_snapshot/engineering_inputs/breakdown_snapshot.
// The engineering calculation (calculateRebarSlab) lives in exactly one
// place (quantityEngine.js) and is never duplicated.
// =======================================================================

const BAR_DIAMETERS = [8, 10, 12, 16, 20, 25, 32, 40]

export default function RebarSlabModulePanel({ tenderId, scopeId, boqItemId, linkedItems, superseding, onClose, onSave, onError, saving }) {
  // Draft key is scoped to (context, module) so Slab and Beam drafts
  // never collide, and different Scopes/BOQ items keep separate drafts.
  // Only applies to a brand-new Output — recalculating an existing one
  // (superseding present) always starts from that Output's own saved
  // data, never from an unrelated stale draft.
  const draftKey = `draft:rebar_slab:${scopeId || boqItemId || 'unknown'}`
  const [draft, setDraft, clearDraft] = useDraftPersistence(draftKey, {
    selectedSlabIds: [],
    openingsBySlab: {},
    layers: [],
    covers: { top: '', bottom: '' },
    lapPct: '',
    wastePct: '',
    selectedBasis: 'procurement',
  })

  const initial = superseding
    ? {
        selectedSlabIds: superseding.geometry_snapshot?.items?.map((i) => i.takeoff_item_id) || [],
        openingsBySlab: superseding.engineering_inputs?.openingsBySlab || {},
        layers: superseding.engineering_inputs?.layers || [],
        covers: superseding.engineering_inputs?.covers || { top: '', bottom: '' },
        lapPct: superseding.engineering_inputs?.lapPct ?? '',
        wastePct: superseding.engineering_inputs?.wastePct ?? '',
        selectedBasis: 'procurement',
      }
    : draft

  const [selectedSlabIds, setSelectedSlabIdsRaw] = useState(new Set(initial.selectedSlabIds))
  const [resolvedSlabs, setResolvedSlabs] = useState({})
  const [openingsBySlab, setOpeningsBySlabRaw] = useState(initial.openingsBySlab)
  const [layers, setLayersRaw] = useState(initial.layers)
  const [covers, setCoversRaw] = useState(initial.covers)
  const [lapPct, setLapPctRaw] = useState(initial.lapPct)
  const [wastePct, setWastePctRaw] = useState(initial.wastePct)
  const [selectedBasis, setSelectedBasis] = useState(initial.selectedBasis)

  // Every setter also writes through to the persisted draft — this is
  // the ONLY place draft-writing happens; the rest of the component is
  // unchanged from before draft persistence existed.
  const setSelectedSlabIds = (updater) => {
    setSelectedSlabIdsRaw((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater
      if (!superseding) setDraft((d) => ({ ...d, selectedSlabIds: [...next] }))
      return next
    })
  }
  const setOpeningsBySlab = (updater) => {
    setOpeningsBySlabRaw((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater
      if (!superseding) setDraft((d) => ({ ...d, openingsBySlab: next }))
      return next
    })
  }
  const setLayers = (updater) => {
    setLayersRaw((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater
      if (!superseding) setDraft((d) => ({ ...d, layers: next }))
      return next
    })
  }
  const setCovers = (updater) => {
    setCoversRaw((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater
      if (!superseding) setDraft((d) => ({ ...d, covers: next }))
      return next
    })
  }
  const setLapPct = (v) => { setLapPctRaw(v); if (!superseding) setDraft((d) => ({ ...d, lapPct: v })) }
  const setWastePct = (v) => { setWastePctRaw(v); if (!superseding) setDraft((d) => ({ ...d, wastePct: v })) }

  // Resolve real Length/Width for every selected slab from its Rectangle
  // measurement's geometry_data + the calibration active at capture time.
  useEffect(() => {
    const idsToResolve = [...selectedSlabIds].filter((id) => !resolvedSlabs[id])
    if (idsToResolve.length === 0) return
    ;(async () => {
      for (const id of idsToResolve) {
        const item = linkedItems.find((r) => r.id === id)
        const { data: measurements, error } = await supabase
          .from('measurement')
          .select('geometry_data, calibration_id')
          .eq('takeoff_item_id', id)
          .eq('is_active', true)
          .order('created_at', { ascending: false })
          .limit(1)
        if (error) { onError(error.message); continue }
        const m = measurements?.[0]
        if (!m || m.geometry_data?.type !== 'rectangle') {
          setResolvedSlabs((prev) => ({ ...prev, [id]: { error: 'No Rectangle measurement found — Length/Width cannot be recovered.' } }))
          continue
        }
        const { data: cal, error: calError } = await supabase
          .from('sheet_calibration').select('real_distance, pixel_distance').eq('id', m.calibration_id).single()
        if (calError || !cal) {
          setResolvedSlabs((prev) => ({ ...prev, [id]: { error: 'Calibration used for this measurement could not be found.' } }))
          continue
        }
        const scaleFactor = cal.real_distance / cal.pixel_distance
        const length = Number(m.geometry_data.pdf_width) * scaleFactor
        const width = Number(m.geometry_data.pdf_height) * scaleFactor
        setResolvedSlabs((prev) => ({ ...prev, [id]: { label: item?.description || id, length, width, area: length * width } }))
      }
    })()
  }, [selectedSlabIds, linkedItems, resolvedSlabs, onError])

  function toggleSlab(id) {
    setSelectedSlabIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function addLayer() {
    setLayers((prev) => [...prev, {
      id: `L${Date.now()}`, label: '', direction: 'X', diameter: '', spacing: '', coverPosition: 'bottom',
      extentType: 'full_slab', localExtent: { barLengthDim: '', distributionWidthDim: '' },
      hookAllowance: '', developmentLengthOverride: '',
    }])
  }
  function updateLayer(id, patch) { setLayers((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l))) }
  function removeLayer(id) { setLayers((prev) => prev.filter((l) => l.id !== id)) }

  function addOpening(slabId) {
    setOpeningsBySlab((prev) => ({
      ...prev,
      [slabId]: [...(prev[slabId] || []), { id: `O${Date.now()}`, label: '', offsetAlongPerpendicular: '', extentAlongPerpendicular: '' }],
    }))
  }
  function updateOpening(slabId, openingId, patch) {
    setOpeningsBySlab((prev) => ({
      ...prev,
      [slabId]: prev[slabId].map((o) => (o.id === openingId ? { ...o, ...patch } : o)),
    }))
  }
  function removeOpening(slabId, openingId) {
    setOpeningsBySlab((prev) => ({ ...prev, [slabId]: prev[slabId].filter((o) => o.id !== openingId) }))
  }

  const slabsForCalc = useMemo(() => {
    return [...selectedSlabIds]
      .map((id) => {
        const resolved = resolvedSlabs[id]
        if (!resolved || resolved.error) return null
        const openings = (openingsBySlab[id] || [])
          .filter((o) => o.offsetAlongPerpendicular !== '' && o.extentAlongPerpendicular !== '')
          .map((o) => ({ offsetAlongPerpendicular: Number(o.offsetAlongPerpendicular), extentAlongPerpendicular: Number(o.extentAlongPerpendicular) }))
        return { ...resolved, id, openings }
      })
      .filter(Boolean)
  }, [selectedSlabIds, resolvedSlabs, openingsBySlab])

  const layersForCalc = useMemo(() => layers.map((l) => ({
    ...l,
    diameter: l.diameter === '' ? NaN : Number(l.diameter),
    spacing: l.spacing === '' ? NaN : Number(l.spacing),
    hookAllowance: l.hookAllowance === '' ? 0 : Number(l.hookAllowance),
    developmentLengthOverride: l.developmentLengthOverride === '' ? 0 : Number(l.developmentLengthOverride),
    localExtent: l.extentType === 'local'
      ? { barLengthDim: l.localExtent.barLengthDim === '' ? NaN : Number(l.localExtent.barLengthDim), distributionWidthDim: l.localExtent.distributionWidthDim === '' ? NaN : Number(l.localExtent.distributionWidthDim) }
      : undefined,
  })), [layers])

  const coversForCalc = useMemo(() => ({
    top: covers.top === '' ? NaN : Number(covers.top),
    bottom: covers.bottom === '' ? NaN : Number(covers.bottom),
  }), [covers])

  const result = useMemo(() => {
    try {
      return { value: calculateRebarSlab(slabsForCalc, layersForCalc, coversForCalc, {
        lapPct: lapPct === '' ? NaN : Number(lapPct), wastePct: wastePct === '' ? NaN : Number(wastePct),
      }), error: null }
    } catch (e) {
      return { value: null, error: e.message }
    }
  }, [slabsForCalc, layersForCalc, coversForCalc, lapPct, wastePct])

  const hasUnresolvedError = [...selectedSlabIds].some((id) => resolvedSlabs[id]?.error)

  async function handleSave() {
    if (!result.value) return
    const chosen = result.value.quantityBases[selectedBasis]

    const geometrySnapshot = {
      items: slabsForCalc.map((s) => ({
        takeoff_item_id: s.id, measurement_type: 'area', value: s.area, unit: 'm2', label: s.label,
        detail: { length: s.length, width: s.width, openings: s.openings },
      })),
    }
    const engineeringInputs = { layers, covers, lapPct: Number(lapPct), wastePct: Number(wastePct), openingsBySlab, quantityBasisChosen: selectedBasis }

    // The caller decides HOW to persist this (BOQ resolved_quantity via
    // recapture_quantity_source, or a Resource Quantity Output via
    // record_resource_quantity_output) — this component only produces
    // the calculated payload, never the persistence mechanism itself.
    // onSave resolves to `false` on failure so the draft is preserved —
    // a failed save must never silently discard the estimator's work.
    const succeeded = await onSave({
      calculatedValueTon: chosen.valueTon,
      unit: 'ton',
      geometrySnapshot,
      engineeringInputs,
      breakdownSnapshot: { steps: result.value.steps, slabBreakdown: result.value.slabBreakdown, quantityBases: result.value.quantityBases },
      takeoffItemIdForProvenance: [...selectedSlabIds][0] || null, // first slab, for BOQ's single takeoff_item_id reference
    })
    // A successful save means this configuration is now persisted as an
    // Output — the temporary draft no longer represents "unsaved work"
    // and must be cleared, not confused with the Output's own history.
    if (succeeded !== false) clearDraft()
  }

  return (
    <div>

      <div style={{ padding: '8px 10px', background: '#FFF8E1', border: `1px solid #F5C542`, borderRadius: 6, fontSize: 11, color: '#7A5C00', marginBottom: 14 }}>
        ⚠ Openings deduct intersecting bars only. Trimming/edge reinforcement is NOT calculated automatically — add it as a separate Layer with Extent = "Local" if required.
      </div>

      {/* ===== Slab selection ===== */}
      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', marginBottom: 5 }}>Select Slab(s)</label>
      <div style={{ border: `1px solid ${colors.border}`, borderRadius: 6, maxHeight: 130, overflowY: 'auto', marginBottom: 14 }}>
        {linkedItems.length === 0 && <div style={{ padding: 10, fontSize: 12, color: colors.textMuted }}>No geometry linked to this scope yet.</div>}
        {linkedItems.filter((it) => it.measurement_type === 'area').map((item) => {
          const resolved = resolvedSlabs[item.id]
          return (
            <label key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderBottom: `1px solid ${colors.border}`, fontSize: 12, cursor: 'pointer' }}>
              <input type="checkbox" checked={selectedSlabIds.has(item.id)} onChange={() => toggleSlab(item.id)} />
              <span style={{ flex: 1 }}>{item.description}</span>
              {selectedSlabIds.has(item.id) && resolved && (
                resolved.error
                  ? <span style={{ color: colors.danger, fontSize: 10 }}>{resolved.error}</span>
                  : <span style={{ color: colors.textMuted, fontSize: 11 }}>{formatNumber(resolved.length)} × {formatNumber(resolved.width)} m</span>
              )}
            </label>
          )
        })}
      </div>

      {/* ===== Openings per selected slab ===== */}
      {slabsForCalc.map((slab) => (
        <div key={slab.id} style={{ marginBottom: 10, padding: 8, border: `1px dashed ${colors.border}`, borderRadius: 6 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: colors.textSecondary }}>Openings — {slab.label}</span>
            <Button size="sm" variant="secondary" onClick={() => addOpening(slab.id)}>+ Add Opening</Button>
          </div>
          {(openingsBySlab[slab.id] || []).map((op) => (
            <div key={op.id} style={{ display: 'flex', gap: 6, marginBottom: 4, alignItems: 'center' }}>
              <Input placeholder="Label" value={op.label} onChange={(e) => updateOpening(slab.id, op.id, { label: e.target.value })} style={{ flex: 1, fontSize: 11 }} />
              <Input type="number" placeholder="Offset (m)" value={op.offsetAlongPerpendicular} onChange={(e) => updateOpening(slab.id, op.id, { offsetAlongPerpendicular: e.target.value })} style={{ width: 90, fontSize: 11 }} />
              <Input type="number" placeholder="Extent (m)" value={op.extentAlongPerpendicular} onChange={(e) => updateOpening(slab.id, op.id, { extentAlongPerpendicular: e.target.value })} style={{ width: 90, fontSize: 11 }} />
              <button onClick={() => removeOpening(slab.id, op.id)} style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer' }}>✕</button>
            </div>
          ))}
        </div>
      ))}

      {/* ===== Covers ===== */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: colors.textSecondary, marginBottom: 5 }}>Top Cover (mm) — required</label>
          <Input type="number" value={covers.top} onChange={(e) => setCovers((p) => ({ ...p, top: e.target.value }))} style={{ width: '100%' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: colors.textSecondary, marginBottom: 5 }}>Bottom Cover (mm) — required</label>
          <Input type="number" value={covers.bottom} onChange={(e) => setCovers((p) => ({ ...p, bottom: e.target.value }))} style={{ width: '100%' }} />
        </div>
      </div>

      {/* ===== Layers ===== */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <label style={{ fontSize: 11, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase' }}>Reinforcement Layers</label>
        <Button size="sm" variant="secondary" onClick={addLayer}>+ Add Layer</Button>
      </div>
      {layers.length === 0 && <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 14 }}>No layers yet — add at least one.</div>}
      {layers.map((l) => (
        <div key={l.id} style={{ border: `1px solid ${colors.border}`, borderRadius: 6, padding: 10, marginBottom: 8 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <Input placeholder="Layer name" value={l.label} onChange={(e) => updateLayer(l.id, { label: e.target.value })} style={{ flex: 1, fontSize: 12 }} />
            <button onClick={() => removeLayer(l.id)} style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer' }}>✕</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 6, marginBottom: 6 }}>
            <Select value={l.direction} onChange={(e) => updateLayer(l.id, { direction: e.target.value })} style={{ fontSize: 11 }}>
              <option value="X">Direction X</option><option value="Y">Direction Y</option>
            </Select>
            <Select value={l.diameter} onChange={(e) => updateLayer(l.id, { diameter: e.target.value })} style={{ fontSize: 11 }}>
              <option value="" disabled>Ø (mm)</option>
              {BAR_DIAMETERS.map((d) => <option key={d} value={d}>Ø{d}</option>)}
            </Select>
            <Input type="number" placeholder="Spacing (mm)" value={l.spacing} onChange={(e) => updateLayer(l.id, { spacing: e.target.value })} style={{ fontSize: 11 }} />
            <Select value={l.coverPosition} onChange={(e) => updateLayer(l.id, { coverPosition: e.target.value })} style={{ fontSize: 11 }}>
              <option value="top">Top Cover</option><option value="bottom">Bottom Cover</option>
            </Select>
            <Select value={l.extentType} onChange={(e) => updateLayer(l.id, { extentType: e.target.value })} style={{ fontSize: 11 }}>
              <option value="full_slab">Full Slab</option><option value="local">Local Extent</option>
            </Select>
          </div>
          {l.extentType === 'local' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 6 }}>
              <Input type="number" placeholder="Bar Length Dim (m)" value={l.localExtent.barLengthDim} onChange={(e) => updateLayer(l.id, { localExtent: { ...l.localExtent, barLengthDim: e.target.value } })} style={{ fontSize: 11 }} />
              <Input type="number" placeholder="Distribution Width Dim (m)" value={l.localExtent.distributionWidthDim} onChange={(e) => updateLayer(l.id, { localExtent: { ...l.localExtent, distributionWidthDim: e.target.value } })} style={{ fontSize: 11 }} />
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            <Input type="number" placeholder="Hook Allowance (mm, optional)" value={l.hookAllowance} onChange={(e) => updateLayer(l.id, { hookAllowance: e.target.value })} style={{ fontSize: 11 }} />
            <Input type="number" placeholder="Development Length Override (mm, optional)" value={l.developmentLengthOverride} onChange={(e) => updateLayer(l.id, { developmentLengthOverride: e.target.value })} style={{ fontSize: 11 }} />
          </div>
        </div>
      ))}

      {/* ===== Lap / Waste ===== */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10, marginBottom: 14 }}>
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: colors.textSecondary, marginBottom: 5 }}>Lap/Splice % (simplified estimating allowance) — required</label>
          <Input type="number" value={lapPct} onChange={(e) => setLapPct(e.target.value)} style={{ width: '100%' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: colors.textSecondary, marginBottom: 5 }}>Waste % (manufacturing/cutting waste only) — required</label>
          <Input type="number" value={wastePct} onChange={(e) => setWastePct(e.target.value)} style={{ width: '100%' }} />
        </div>
      </div>

      {/* ===== Breakdown ===== */}
      {result.value && (
        <div style={{ marginBottom: 14 }}>
          {result.value.slabBreakdown.map((sb, si) => (
            <div key={si} style={{ border: `1px solid ${colors.border}`, borderRadius: 8, padding: 10, marginBottom: 8, background: '#FAFBFC' }}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>{sb.slab.label} — {formatNumber(sb.slab.length)} × {formatNumber(sb.slab.width)} m</div>
              {sb.layerResults.map((lr, li) => (
                <div key={li} style={{ fontSize: 11, color: colors.textSecondary, padding: '4px 0', borderTop: li > 0 ? `1px dashed ${colors.border}` : 'none' }}>
                  <div style={{ fontWeight: 700, color: colors.textPrimary }}>{lr.layer.label || `Layer ${li + 1}`} ({lr.layer.extentType}, Dir {lr.layer.direction}, Ø{lr.layer.diameter}@{lr.layer.spacing}mm)</div>
                  <div>Eff.Dim: {formatNumber(lr.effectiveDimension)}m · Bars: {lr.numberOfBars} · Actual Spacing: {formatNumber(lr.actualSpacing * 1000)}mm</div>
                  {lr.deductedBars > 0 && <div>Deducted (openings): {lr.deductedBars} → Final Bars: {lr.finalNumberOfBars}</div>}
                  <div>Cutting Length: {formatNumber(lr.cuttingLength)}m · Total: {formatNumber(lr.totalLength)}m · <strong>Gross: {formatNumber(lr.grossWeight)} kg</strong></div>
                </div>
              ))}
              <div style={{ marginTop: 6, paddingTop: 6, borderTop: `1px solid ${colors.border}`, fontSize: 12, fontWeight: 700 }}>Slab Gross: {formatNumber(sb.slabGross)} kg</div>
            </div>
          ))}
          <div style={{ padding: '10px 12px', background: '#F8FAFF', border: `1px solid ${colors.primary}`, borderRadius: 8 }}>
            {result.value.steps.map((s, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0' }}>
                <span style={{ color: colors.textSecondary }}>{s.label}</span>
                <span style={{ fontWeight: 600 }}>{formatNumber(s.value)} {s.unit}</span>
              </div>
            ))}
          </div>

          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: colors.textSecondary, marginTop: 12, marginBottom: 5 }}>Save as Resource Quantity Output — choose basis</label>
          <Select value={selectedBasis} onChange={(e) => setSelectedBasis(e.target.value)} style={{ width: '100%' }}>
            <option value="theoretical">{result.value.quantityBases.theoretical.label} — {formatNumber(result.value.quantityBases.theoretical.valueTon)} ton</option>
            <option value="theoreticalPlusLap">{result.value.quantityBases.theoreticalPlusLap.label} — {formatNumber(result.value.quantityBases.theoreticalPlusLap.valueTon)} ton</option>
            <option value="procurement">{result.value.quantityBases.procurement.label} — {formatNumber(result.value.quantityBases.procurement.valueTon)} ton</option>
          </Select>
        </div>
      )}
      {result.error && <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 14, fontStyle: 'italic' }}>{result.error}</div>}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {!superseding && (
          <button
            onClick={() => {
              if (window.confirm('Discard all unsaved input for this module? This cannot be undone.')) {
                clearDraft()
                onClose()
              }
            }}
            style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer', fontSize: 12 }}
          >
            Discard Draft
          </button>
        )}
        <Button onClick={handleSave} disabled={!result.value || saving || hasUnresolvedError} style={{ marginLeft: 'auto' }}>
          {saving ? 'Saving…' : superseding ? 'Save New Version' : 'Save Output'}
        </Button>
      </div>
    </div>
  )
}
